// Profile photo that stays sharp at small sizes.
//
// Uploaded photos are often phone-sized portraits (e.g. 1170x1562). Letting
// the browser squeeze that into a 48px circle in one pass smears it, and a
// centred cover-crop of a tall portrait cuts off the top of the head. Here the
// photo is cropped to a square from the TOP, then halved step by step on a
// canvas (each step a high-quality resample) down to exactly the rendered
// size at 2x the screen's pixel density.
import { useEffect, useState } from "react";

async function renderCrisp(url: string, px: number): Promise<string> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.decoding = "async";
  img.src = url;
  await img.decode();

  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = 0; // keep the top of the head; portraits put the face near the top

  // Start with the square crop at full resolution.
  let canvas = document.createElement("canvas");
  canvas.width = canvas.height = side;
  let ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, sx, sy, side, side, 0, 0, side, side);

  // Halve until one more halving would go below the target, then finish.
  let size = side;
  while (size / 2 >= px) {
    const next = Math.round(size / 2);
    const c = document.createElement("canvas");
    c.width = c.height = next;
    const cx = c.getContext("2d")!;
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = "high";
    cx.drawImage(canvas, 0, 0, size, size, 0, 0, next, next);
    canvas = c;
    ctx = cx;
    size = next;
  }
  if (size !== px) {
    const c = document.createElement("canvas");
    c.width = c.height = px;
    const cx = c.getContext("2d")!;
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = "high";
    cx.drawImage(canvas, 0, 0, size, size, 0, 0, px, px);
    canvas = c;
  }
  return canvas.toDataURL("image/png");
}

export function CrispAvatar({ src, alt, size }: { src: string; alt: string; size: number }) {
  const [crisp, setCrisp] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const px = Math.round(size * Math.max(2, window.devicePixelRatio || 1));
    renderCrisp(src, px)
      .then((d) => { if (!cancelled) setCrisp(d); })
      .catch(() => { if (!cancelled) setCrisp(null); });
    return () => { cancelled = true; };
  }, [src, size]);

  return (
    <img
      src={crisp ?? src}
      alt={alt}
      width={size}
      height={size}
      className="block w-full h-full rounded-full object-cover object-top"
    />
  );
}
